import React, { useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Stack, TextField, Typography, Button } from "@mui/material";
import { useDispatch } from "react-redux";

import Logo from "../../shared/Logo/Logo";
import HeaderBarcode from "../../shared/HeaderBarcode/HeaderBarcode";
import FooterBarcode from "../../shared/FooterBarcode/FooterBarcode";

import { authSyncAPI } from "../../../api/services/authSyncAPI";
import {
  restoreSnapshotToLocalStorage,
  syncOrchestrator,
} from "../../../api/services/syncOrchestrator";
import {
  setAuthFromLogin,
  getMissionAccomplishmentsThunk,
} from "../../../redux/slices/createUserSlice";
import { ensureEligibleThenInitialize } from "../../../redux/slices/exitFlowSlice";
import { markLocalChange } from "../../../utils/gamePersistence";

import "./Login.scss";

const decideExitRedirect = (state, alreadyInitiated = false) => {
  // Mirror your rootLoader mapping
  // early/phase-1 → /gameFinish (your loader currently routes to P2 for all; keep consistent with that)

  if (state === "not_started" && !alreadyInitiated) return "/map";
  if (
    state === "initiated" ||
    state === "montage_done" ||
    state === "details_submitted" ||
    state === "reveal_ready"
  ) {
    return "/gameFinishP2";
  }
  if (state === "reveal_done") return "/finals";

  // Unknown or normal play
  return "/map";
};

const Login = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [systemName, setSystemName] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const [errors, setErrors] = useState({ systemName: "", password: "" });
  const clearFieldError = (field) => setErrors((e) => ({ ...e, [field]: "" }));

  // prevent double-submit races / double sync start
  const didBootstrapRef = useRef(false);

  const handleRemoveLsAndMoveToRoot = () => {
    localStorage.clear();
    sessionStorage.clear();
    navigate("/", { replace: true });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    const trimmedSystem = systemName.trim();
    const trimmedPass = password.trim();

    // client-side required checks
    const nextErrors = { systemName: "", password: "" };
    if (!trimmedSystem) nextErrors.systemName = "System name is required";
    if (!trimmedPass) nextErrors.password = "Password is required";
    if (nextErrors.systemName || nextErrors.password) {
      setErrors(nextErrors);
      return;
    }

    try {
      setSubmitting(true);

      // 1) LOGIN to auth-sync
      const loginRes = await authSyncAPI.login({
        systemName: trimmedSystem,
        password: trimmedPass,
      });

      // From backend: { authToken, syncToken, gameSnapshot }
      const { authToken, syncToken, gameSnapshot } = loginRes || {};

      if (!authToken) {
        setErrors({
          systemName: "",
          password: "Invalid credentials",
        });
        setSubmitting(false);
        return;
      }

      // 2) Persist flat creds for sync headers
      localStorage.setItem("systemName", trimmedSystem);
      localStorage.setItem("authToken", authToken);
      if (syncToken) localStorage.setItem("syncToken", syncToken);

      // 3) Restore server snapshot (resume on new device)
      if (gameSnapshot?.data && typeof gameSnapshot.data === "object") {
        restoreSnapshotToLocalStorage(gameSnapshot.data);
      }

      // 4) Update Redux slice & fetch accomplishments
      dispatch(
        setAuthFromLogin({
          authToken,
          systemName: trimmedSystem,
        })
      );
      if (authToken) {
        dispatch(
          getMissionAccomplishmentsThunk({
            authToken,
            systemName: trimmedSystem,
          })
        );
      }

      // 5) Mark local change & start sync loop (ACK + periodic)
      markLocalChange();
      if (!didBootstrapRef.current) {
        didBootstrapRef.current = true;
        await syncOrchestrator.start();
      }

      // 6) Exit flow check (mirror rootLoader logic)
      try {
        const exitAction = await dispatch(
          ensureEligibleThenInitialize({
            systemName: trimmedSystem,
            authToken,
          })
        );
        console.log("exitAction", exitAction);
        if (ensureEligibleThenInitialize.fulfilled.match(exitAction)) {
          const exitState = exitAction.payload?.state;
          const alreadyInitiated =
            exitAction.payload?.alreadyInitiated ?? false;
          const dest = decideExitRedirect(exitState, alreadyInitiated);
          navigate(dest);
          return;
        }

        // If check failed, be forgiving and go to map
        navigate("/map");
      } catch {
        // Safety: if Exit API errors, fall back to map
        navigate("/map");
      }
    } catch (err) {
      // Show field-specific error messages (don’t cascade)
      const apiMsg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        "Login failed. Please check your credentials.";

      setErrors({
        systemName: "",
        password: apiMsg,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div className="login-container">
        <div className="dotted-background">
          <Stack direction={"column"} className="container header">
            <HeaderBarcode />
          </Stack>

          <Stack gap={2} useFlexGap className="container content">
            <Stack
              useFlexGap
              gap={4}
              className="login-notice-board login-board"
            >
              <Stack>
                <h1 className="login-title">
                  Welcome,
                  <br />
                  Investigator!
                </h1>
                {/* <Typography className="login-subtitle">
                  Enter the password you've received
                </Typography> */}
                <Typography className="login-subtitle">
                  Your investigator code is issued when you create your account.
                  It works as both your username and password keep it safe.
                  You’ll need it to log in and resume your saved progress later.
                </Typography>
              </Stack>

              <form onSubmit={handleSubmit} noValidate autoComplete="off">
                <Stack useFlexGap gap={4}>
                  <Stack useFlexGap gap={1}>
                    <Stack useFlexGap gap={2}>
                      <TextField
                        variant="outlined"
                        placeholder="Enter Investigator Code *"
                        name="systemName"
                        type="text"
                        autoComplete="off"
                        value={systemName}
                        onChange={(e) => {
                          setSystemName(e.target.value);
                          if (errors.systemName) clearFieldError("systemName");
                        }}
                        error={Boolean(errors.systemName)}
                        helperText={errors.systemName || ""}
                        inputProps={{ "aria-label": "System name" }}
                        autoFocus
                        required
                      />

                      <TextField
                        variant="outlined"
                        placeholder="Enter Password *"
                        name="password"
                        type="password"
                        autoComplete="off"
                        value={password}
                        onChange={(e) => {
                          setPassword(e.target.value);
                          if (errors.password) clearFieldError("password");
                        }}
                        error={Boolean(errors.password)}
                        helperText={errors.password || ""}
                        inputProps={{ "aria-label": "Password" }}
                        required
                      />
                    </Stack>

                    <button
                      className="signup-link"
                      // to={"/generateInvestigationCode"}
                      onClick={handleRemoveLsAndMoveToRoot}
                    >
                      Create Account
                    </button>
                  </Stack>

                  <Button
                    type="submit"
                    variant="contained"
                    className="btn-flat"
                    disabled={submitting}
                  >
                    {submitting ? "Signing in..." : "Login"}
                  </Button>
                </Stack>
              </form>
            </Stack>
          </Stack>

          <Stack className="container footer">
            <Logo />
            <FooterBarcode />
          </Stack>
        </div>
      </div>
    </>
  );
};

export default Login;
